import { userService } from '@services/domain-factories/user';
import { initOnce } from '@utils/zustand/initOnce';
import { Member, Role, User } from '@vincerosoft/assessfluence-types';
import { createStoreWithSelector } from '../../utils/zustand/createStoreWithSelector';
import { createUseStore } from '../../utils/zustand/createUseStore';
import { WithReset } from '../../utils/zustand/zustandTypes';

const initialState = {
  isInitialized: false,
  currentMember: null,
  role: null,
  appUser: null,
  memberAccounts: null,
  inFlightInit: null,
  loading: false
};

type State = {
  isInitialized: boolean;
  inFlightInit: Promise<void> | null;
  loading: boolean;
  currentMember: Member | null;
  appUser: User | null;
  role: Role | null;
  memberAccounts: Member[] | null;
  // Bumped by reset() so in-flight async writes from a prior session can detect staleness and no-op.
  resetToken: number;
};

type Action = {
  reset: () => void;
  initUserCtx: (uid: string) => Promise<void>;
  updateUser: (user: User) => void;
  updateMember: (member: Member) => void;
  updateRole: (role: Role) => void;
};

export const appUserStore = createStoreWithSelector<WithReset<State & Action>>()((set, get) => ({
  ...initialState,
  resetToken: 0,
  reset: () => set((state) => ({ ...initialState, resetToken: state.resetToken + 1 })),
  updateUser: (user) => set({ appUser: user }),
  updateMember: (member) => set({ currentMember: member }),
  updateRole: (role) => set({ role }),
  initUserCtx: (uid) =>
    initOnce(
      'isInitialized',
      set,
      get,
      () => userService.initUserContext(uid),
      (userContext) => set({ ...userContext, isInitialized: true })
    )
}));

export const useAppUserStore = createUseStore(appUserStore);
export const userStoreActions = (): Action => appUserStore.getState();
